import { onError } from '@apollo/client/link/error';
import Cookies from 'js-cookie';

import { APIErrorCode } from 'utils/APIErrorCodes/APIErrorCode';
import { config } from 'appConfig';
import { APIErrorCodesCatalog } from 'utils/APIErrorCodes/APIErrorCodesCatalog';
import { logError, ScopeOptions } from 'utils/logging';
import { HttpLink, ApolloLink, from as apolloLinkFrom, ServerError, ServerParseError, Operation } from '@apollo/client';
import { ApolloError, NetworkError } from '@apollo/client/errors';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLFormattedError, OperationDefinitionNode } from 'graphql';
import { isError, isNull } from 'typeDeclarations/typeGuards';
import { isDate } from 'date-fns';
import { resetTracking } from 'components/providers/AppPosthogProvider';

const customFetch: WindowOrWorkerGlobalScope['fetch'] = (uri, options) => {
  const controller = new AbortController();

  // BT-8404 Merge the existing signal with our controller
  const signal = options?.signal ? AbortSignal.any([options.signal, controller.signal]) : controller.signal;

  const result = fetch(uri, options);

  result
    .then((response) => {
      const contentType = response.headers.get('content-type');

      let errorMessage: undefined | string;

      if (isNull(contentType)) {
        errorMessage = 'Instead of JSON, request has no content type/response data';
      } else if (contentType !== 'application/json') {
        response
          .clone()
          .text()
          .then((text) => {
            errorMessage = `Instead of JSON, received request whose content is of type '${contentType}': ${text}`;
          });
      }

      if (errorMessage) {
        const error = new Error(errorMessage);
        error.name = 'Wrong Content Type';

        logError(error, {
          level: 'warning',
          tags: { context: 'httpLink' },
          type: 'Fetch Exception',
          message: errorMessage,
          stack: error.stack,
        });
      }
    })
    .catch((error) => {
      if (error instanceof Error) {
        logError(error, {
          level: 'warning',
          tags: { context: 'httpLink' },
          type: 'Fetch Exception',
          extras: { signalAborted: String(signal.aborted), reason: String(signal.reason || '') },
          message: `Fetch request failed for ${String(uri)}: ${error.message}`,
        });
      }
    });

  return result;
};

const httpLink = new HttpLink({
  fetch: customFetch,
  uri: config.routes.api,
  credentials: 'include',
  headers: {
    accept: 'application/json',
    'X-CSRFToken': Cookies.get(config.cookies.csrf) ?? '',
  },
});

/**
 * see {@link https://www.apollographql.com/docs/react/api/link/introduction#handling-a-response  }
 */
const roundTripLink = new ApolloLink((operation, forward) => {
  // Called before operation is sent to server
  operation.setContext((prevContext: Record<string, unknown>) => ({
    ...prevContext,
    startTs: new Date(),
  }));

  return forward(operation).map((data) => {
    // Called after server responds
    operation.setContext((prevContext: Record<string, unknown>) => ({
      ...prevContext,
      endTs: new Date(),
    }));

    return data;
  });
});

/**
 * see {@link https://www.apollographql.com/docs/react/api/link/apollo-link-retry/}
 * RetryLink does not (currently) handle retries for GraphQL errors in the response, only for network errors.
 */
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, operation) => {
      if (!isError(error)) {
        return false;
      }

      if (!error || !('statusCode' in error)) {
        return false;
      }

      const { statusCode } = error as ServerParseError | ServerError;

      // these status codes are handled properly on the onError link
      if (statusCode === 401 || statusCode === 403) {
        return false;
      }

      // time to discover the operation type (query, mutation, subscription)
      const {
        getContext,
        query: { definitions },
      } = operation;

      if (!definitions.length) {
        return false;
      }

      const operationDefinition = definitions.find(({ kind }) => kind === 'OperationDefinition');

      if (!operationDefinition) {
        return false;
      }

      const opDef = operationDefinition as OperationDefinitionNode;

      // only graphql query (non-idempotent) operations should be retried
      if (opDef.operation !== 'query') {
        return false;
      }

      // all 4.x.x error codes should be immediately retried
      if (statusCode >= 400 && statusCode < 500) {
        return true;
      }

      if (statusCode >= 500 && statusCode < 600) {
        const opContext = getContext();

        const { startTs, endTs } = opContext;

        if (isDate(startTs) && isDate(endTs)) {
          const elapsedTimeInSec = Math.floor((endTs - startTs) / 1000);

          // only retry requests that took up to 1min
          if (elapsedTimeInSec <= 60) {
            return true;
          }
        }
      }

      return false;
    },
  },
});

function loginRedirect() {
  resetTracking();
  window.location.href = config.routes.login;
}

/**
 * graphQLErrors is an alias for networkError.result.errors in the case of a network error
 * @see {@link https://www.apollographql.com/docs/link/links/error/#error-categorization}
 */
function logGraphQLErrors(graphQLErrors: readonly GraphQLFormattedError[], operation: Operation, response: string) {
  const filteredGQLErrors = graphQLErrors?.filter(({ extensions }) => !extensions?.is_user_error);
  if (filteredGQLErrors?.length) {
    const error = new ApolloError({ graphQLErrors: filteredGQLErrors });
    error.name = 'GraphQL Error';

    const scopeOptions: ScopeOptions = {
      level: 'fatal',
      tags: { context: 'errorLink' },
      fingerprint: ['{{ default }}', operation.operationName],
      type: 'GraphQL Error',
      operationName: operation.operationName,
      query: operation.query.loc?.source.body,
      variables: operation.variables,
      response: response,
      message: error.message,
      path: filteredGQLErrors[0].path?.join('.'),
      extras: { errors: JSON.stringify(filteredGQLErrors) },
      stack: error.stack,
    };

    logError(error, scopeOptions);
  }
}

function logNetworkError(networkError: NetworkError, operation: Operation, response: string) {
  if (!networkError) return;

  const statusCode = 'statusCode' in networkError ? networkError.statusCode : '';
  const bodyText = 'bodyText' in networkError ? networkError.bodyText : '';

  const error = new ApolloError({ networkError });
  error.name = 'Network Error';

  const scopeOptions: ScopeOptions = {
    level: 'fatal',
    operationName: operation.operationName,
    query: operation.query.loc?.source.body || '',
    variables: operation.variables,
    message: error.message,
    type: 'Network Error',
    extras: { statusCode: statusCode, bodyText: bodyText },
    stack: networkError?.stack || '',
    tags: { context: 'errorLink' },
    fingerprint: ['{{ default }}', operation.operationName],
    response: response,
  };

  logError(error, scopeOptions);
}

const errorLink = onError(({ response, operation, networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    const errorsCatalog = new APIErrorCodesCatalog(graphQLErrors);

    /**
     * @author bra
     * The exception 'operation.operationName !== "loginMutation"' is for now needed because
     * the login mutation can bring more data which can only be retrieved if the user is logged-in
     * otherwise, the backend sends a NotLoggedIn error for instance if wrong credentials were
     * inserted. This optimization will be debated soon™️ because exceptions likes this will
     * inevitably degrade the code quality
     */
    if (
      errorsCatalog.hasErrorCode(APIErrorCode.CSRFFailed) ||
      (errorsCatalog.hasErrorCode(APIErrorCode.NotLoggedIn) && operation.operationName !== 'loginMutation')
    ) {
      loginRedirect();
      return;
    }

    if (
      operation.operationName === 'prepareAndorraCampaignTreeMutation' &&
      errorsCatalog.hasErrorCode(APIErrorCode.OperationError)
    )
      return;

    if (errorsCatalog.hasErrorCode(APIErrorCode.ChargeRequiresActions)) return;

    const responseString = response ? JSON.stringify(response) : '';

    logGraphQLErrors(graphQLErrors, operation, responseString);
  }

  // Handle networkError
  if (networkError) {
    if ('statusCode' in networkError && (networkError.statusCode === 401 || networkError.statusCode === 403)) {
      loginRedirect();
      return;
    }

    const responseString = response ? JSON.stringify(response) : '';

    logNetworkError(networkError, operation, responseString);
  }

  return;
});

export const link = apolloLinkFrom([errorLink, retryLink, roundTripLink, httpLink]);
